import React, { useEffect, useState, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  Alert,
  Box,
  CircularProgress,
  Typography
} from "@mui/material";
import SurveySlackCard from "./survey-slack-card/survey-card";
import axios from "axios";
import {
  deleteSurveyThunk,
  getAllUserSurveysThunk
} from "../../../redux/survey/survey.thunk";
import ErrorBoundary from "../../../azure_auth/ErrorBoundary";

/**
 * Componente funcional que reemplaza al antiguo class‑component `SurveySlackIndex`.
 * Usa hooks de React y Redux para manejar side‑effects y estado global.
 */
const SurveySlackIndex = () => {
  const dispatch = useDispatch();

  // --- Datos del store ---
  const reduxUser = useSelector(state => state.users.user);
  const allSlackSurveys = useSelector(state => state.surveys.allSurveys);
  const status = useSelector(state => state.surveys.status);

  // --- Estado local ---
  const [sending, setSending] = useState({}); // { [surveyId]: boolean }
  const [toast, setToast] = useState({ open: false, status: "", msg: "" });
  const [globalError, setGlobalError] = useState(null);

  // --- Carga inicial de encuestas ---
  useEffect(() => {
    if (!reduxUser?.id) {
      setGlobalError("No user found in Redux store");
      return;
    }

    const fetchSurveys = async () => {
      try {
        await dispatch(getAllUserSurveysThunk(reduxUser.id));
      } catch (err) {
        console.error(err);
        setGlobalError(err.message);
      }
    };

    fetchSurveys();
  }, [reduxUser?.id, dispatch]);

  // --- Envía una encuesta al equipo ---
  const sendSurveyToTeam = useCallback(
    async (id, isBatabot = false) => {
      if (!reduxUser?.tenantId) {
        setGlobalError("No tenantId found for user");
        return;
      }

      setSending(prev => ({ ...prev, [id]: true }));

      try {
        const url = `${process.env.REACT_APP_BOT_URL}/api/notify/${reduxUser.tenantId}/${id}${
          isBatabot ? "?batabot=true" : ""
        }`;

        const res = await axios.get(url);
        if (res.status !== 200)
          throw new Error(`Notify API returned ${res.status}`);

        setToast({ open: true, status: "success", msg: "Survey sent successfully" });
      } catch (err) {
        console.error(err);
        setToast({ open: true, status: "error", msg: err.message });
        setGlobalError(err.message);
      } finally {
        setSending(prev => ({ ...prev, [id]: false }));
      }
    },
    [reduxUser]
  );

  // --- Elimina una encuesta ---
  const destroySurvey = useCallback(
    async id => {
      if (!reduxUser?.id) {
        setGlobalError("No user found in Redux store");
        return;
      }
      try {
        await dispatch(deleteSurveyThunk(id));
        dispatch(getAllUserSurveysThunk(reduxUser.id));
      } catch (err) {
        console.error(err);
        setGlobalError(err.message);
      }
    },
    [reduxUser?.id, dispatch]
  );

  // --- Vista si falta usuario ---
  if (!reduxUser?.id) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">
          No hay usuario disponible para cargar las encuestas.
        </Alert>
      </Box>
    );
  }

  return (
    <ErrorBoundary sectionName="SurveySlackIndex">
      <Box sx={{ p: 2 }}>
        {/* Error global */}
        {globalError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {globalError}
          </Alert>
        )}

        {/* Toast operaciones */}
        {toast.open && (
          <Alert
            severity={toast.status}
            onClose={() => setToast({ ...toast, open: false })}
            sx={{ mb: 2 }}
          >
            {toast.msg}
          </Alert>
        )}

        {/* Contenido principal */}
        <ErrorBoundary sectionName="SurveySlackCard of SurveySlackIndex">
          {!allSlackSurveys ? (
            <Box className="text-center p-4" sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', paddingTop: '80px', gap: 2 }}>
              <CircularProgress size={40} thickness={4} />
              <Typography>Cargando encuestas…</Typography>
            </Box>
          ) : (
            <SurveySlackCard
              surveys={allSlackSurveys}
              status={status}
              sendSurveyToTeam={sendSurveyToTeam}
              deleteSurvey={destroySurvey}
              sending={sending}
            />
          )}
        </ErrorBoundary>
      </Box>
    </ErrorBoundary>
  );
};

export default SurveySlackIndex;
